import styled from "styled-components";

export const DocumentsSection = styled.section`
  padding: 60px 0 80px;
  background: #f6f8fb;
  font-family: "Segoe UI", sans-serif;
  color: #0a1f3a;

  .doc-container {
    max-width: 980px;
    margin: 0 auto;
    padding: 0 16px;
  }

  .doc-container.wide {
    max-width: 1180px;
  }

  @media (max-width: 768px) {
    padding: 32px 0 48px;

    .doc-container,
    .doc-container.wide {
      padding: 0 12px;
    }
  }
`;

export const HeroCard = styled.div`
  background: linear-gradient(135deg, #0a2952 0%, #143a73 100%);
  border-radius: 18px;
  padding: 32px 36px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  box-shadow: 0 10px 30px rgba(10, 41, 82, 0.18);

  .hero-text {
    flex: 1;
    min-width: 0;
  }

  .hero-title {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.2;
    margin: 0 0 8px;
    color: #fff;
  }

  .hero-subtitle {
    font-size: 14px;
    color: #cdd6e4;
    margin: 0;
    line-height: 1.5;
  }

  .hero-stats {
    display: flex;
    gap: 36px;
    flex-shrink: 0;
  }

  .stat {
    text-align: center;
    min-width: 64px;
  }

  .stat-num {
    font-size: 32px;
    font-weight: 700;
    line-height: 1;
    color: #fff;
  }

  .stat-label {
    margin-top: 6px;
    font-size: 11px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #9bb0d0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    gap: 24px;

    .hero-stats {
      width: 100%;
      justify-content: space-between;
      gap: 12px;
    }

    .hero-title {
      font-size: 22px;
    }
  }
`;

export const Block = styled.section`
  margin-top: 32px;

  @media (max-width: 600px) {
    margin-top: 24px;
  }

  .block-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e3e8f0;
  }

  .block-head .swatch {
    width: 14px;
    height: 14px;
    border-radius: 4px;
    display: inline-block;
    flex-shrink: 0;
  }

  .block-head .swatch.blue { background: #d6e4ff; }
  .block-head .swatch.green { background: #d4f1e0; }
  .block-head .swatch.olive { background: #e6efd9; }
  .block-head .swatch.peach { background: #fde3cd; }
  .block-head .swatch.lavender { background: #e6e1f5; }
  .block-head .swatch.teal { background: #d4ecef; }

  .block-title {
    font-size: 16px;
    font-weight: 600;
    color: #0a1f3a;
    margin: 0;
  }

  .block-count {
    font-size: 13px;
    color: #7a8aa0;
    font-weight: 400;
  }
`;

export const DocRow = styled.div`
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
    border-color: #c5d2e6;
    box-shadow: 0 4px 14px rgba(10, 41, 82, 0.06);
  }

  &[href]:hover {
    border-color: #2a5dbb;
    transform: translateY(-1px);
    cursor: pointer;
  }

  .doc-icon {
    width: 36px;
    height: 36px;
    background: #eaf0fb;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #2a5dbb;
    flex-shrink: 0;
    font-size: 16px;
  }

  .doc-body {
    flex: 1;
    min-width: 0;
  }

  .doc-title {
    font-size: 14px;
    font-weight: 600;
    color: #0a1f3a;
    line-height: 1.35;
    margin: 0 0 4px;
  }

  .doc-meta {
    font-size: 12px;
    color: #6f7e94;
    margin: 0;
    line-height: 1.4;
  }

  .doc-aside {
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    padding: 14px;
    gap: 12px;

    .doc-aside {
      align-self: flex-start;
    }
  }
`;

export const RowGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f1f4f9;
  color: #5a6b85;
  letter-spacing: 0.2px;
  white-space: nowrap;

  &.valid {
    background: #e6f6ec;
    color: #1f8a4a;
  }

  &.status-active {
    background: #e6f6ec;
    color: #1f8a4a;

    &::before {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #1f8a4a;
    }
  }

  &.status-suspended {
    background: #eef0f4;
    color: #6f7e94;

    &::before {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #6f7e94;
    }
  }
`;

export const OperatorsTableWrap = styled.div`
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  overflow: hidden;
  position: relative;

  .ant-table {
    background: transparent;
    font-size: 13px;
  }

  .ant-table-thead > tr > th {
    background: #fafbfd;
    color: #6f7e94;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    border-bottom: 1px solid #e6ebf2;
    padding: 14px 20px;
  }

  .ant-table-tbody > tr > td {
    padding: 16px 20px;
    border-bottom: 1px solid #f0f3f8;
    color: #0a1f3a;
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #fafbfd;
  }

  .col-no {
    color: #a3aec1;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }

  .ant-table-thead > tr > th.nowrap,
  .ant-table-tbody > tr > td.nowrap {
    white-space: nowrap;
  }

  .col-date,
  .col-license {
    color: #6f7e94;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .col-brand {
    color: #0a1f3a;
    font-weight: 500;
  }

  .col-brand.empty {
    color: #c1c8d4;
  }

  .col-website a {
    color: #1f6dd0;
    word-break: break-word;
  }

  .col-website a:hover {
    color: #143a73;
    text-decoration: underline;
  }

  .col-address {
    color: #4a5a72;
    font-size: 12.5px;
    line-height: 1.45;
  }

  .col-name {
    color: #0a1f3a;
    font-weight: 500;
    line-height: 1.35;
  }

  .ant-table-body {
    -webkit-overflow-scrolling: touch;
  }

  .ant-table-body::-webkit-scrollbar,
  .ant-table-content::-webkit-scrollbar {
    height: 6px;
  }
  .ant-table-body::-webkit-scrollbar-track,
  .ant-table-content::-webkit-scrollbar-track {
    background: #f0f3f8;
  }
  .ant-table-body::-webkit-scrollbar-thumb,
  .ant-table-content::-webkit-scrollbar-thumb {
    background: #c5d2e6;
    border-radius: 3px;
  }
  .ant-table-body::-webkit-scrollbar-thumb:hover,
  .ant-table-content::-webkit-scrollbar-thumb:hover {
    background: #2a5dbb;
  }

  @media (max-width: 768px) {
    .ant-table-thead > tr > th {
      padding: 10px 12px;
      font-size: 10px;
      letter-spacing: 0.4px;
    }

    .ant-table-tbody > tr > td {
      padding: 12px;
    }

    .col-name { font-size: 12px; }
    .col-address { font-size: 11.5px; }
  }

  .ant-pagination {
    padding: 12px 20px;
    margin: 0 !important;
    border-top: 1px solid #f0f3f8;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 6px;
  }

  @media (max-width: 600px) {
    .ant-pagination {
      padding: 10px 12px;
      justify-content: center;
    }

    .source-note {
      padding: 10px 12px;
      font-size: 11px;
    }
  }

  .ant-pagination .ant-pagination-prev,
  .ant-pagination .ant-pagination-next,
  .ant-pagination .ant-pagination-item,
  .ant-pagination .ant-pagination-jump-prev,
  .ant-pagination .ant-pagination-jump-next {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
  }

  .ant-pagination .ant-pagination-prev a,
  .ant-pagination .ant-pagination-next a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  .source-note {
    padding: 10px 20px;
    font-size: 12px;
    color: #6f7e94;
    border-top: 1px solid #f0f3f8;
    background: #fafbfd;
  }
`;

export const SectionBottom = styled.section`
  padding-top: 100px;
  padding-bottom: 100px;
  background-color: #072344;
  
  
  & > div > div > div{
    height: calc( 250px - 8px );
    @media (max-width: 991px){
      &:last-child{
        margin-top: 15px;
        height: auto;
      }
    }
    .wrap-block{
      height: 100%;
    }
  }
  
  
  & > div > div>div:first-child{
    
    .wrap-block{
      background-image: url('/images/banners/1.jpg');
      position: relative;
      &:hover{
        &::before{
          opacity: .9;
        }
      }
      &::before{
        position: absolute;
        content: "";
        display: block;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0.8;
        transition: 0.5s;
        background: aqua;
        background: linear-gradient(to bottom right, #031c3a, aqua);
      }
    }
  }

  & > div > div>div:last-child{
    .wrap-block{
      background: #0d3664;
    }
  }
  
  .wrap-block{
    padding: 40px;
    & .text-block{
      position: relative;
      z-index: 3;
      h4{
        color: #fff;
        margin-bottom: 20px;
        font-weight: bold;
      }
      P{
        color: #fff;
      }
    }
    
    a{
      position: absolute;
      bottom: 20px;
      right: 30px;
      color: #fff;
      font-size: 30px;
      opacity: 0.6;
      width: 25px;
      transition: .3s;
      &:hover{
        opacity: 1;
      }
      & svg{
        fill: #fff;
      }
    }
  }
`
